/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { useEffect, useState } from 'react'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

const PREDEFINED_TITLES = ['Dr.', 'Prof.']

interface LeadTitleSelectProps {
    title: string | null | undefined
    customTitle: string | null | undefined
    onChange: (title: string, customTitle: string) => void
    className?: string
    label?: string
}

const LeadTitleSelect: React.FC<LeadTitleSelectProps> = ({
    title,
    customTitle,
    onChange,
    className,
    label,
}) => {
    const { t } = useTranslation()

    const getDisplayValue = (t: string | null | undefined, ct: string | null | undefined) =>
        ((t === 'other' ? ct : t) || '')

    const [query, setQuery] = useState(() => getDisplayValue(title, customTitle))

    useEffect(() => {
        setQuery(getDisplayValue(title, customTitle))
    }, [title, customTitle])

    const safeQuery = query || ''

    const filteredOptions =
        safeQuery === '' || PREDEFINED_TITLES.includes(safeQuery)
            ? PREDEFINED_TITLES
            : PREDEFINED_TITLES.filter((o) => o.toLowerCase().includes(safeQuery.toLowerCase()))

    const currentValue = getDisplayValue(title, customTitle)
    const isCustomQuery = safeQuery.trim() !== '' && !PREDEFINED_TITLES.includes(safeQuery.trim()) && safeQuery.trim() !== currentValue

    const handleOptionSelect = (option: string) => {
        onChange(option, '')
        setQuery(option)
    }

    const handleConfirm = () => {
        const trimmed = query.trim()
        if (!trimmed) return
        if (PREDEFINED_TITLES.includes(trimmed)) {
            onChange(trimmed, '')
        } else {
            onChange('other', trimmed)
        }
    }

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        onChange('', '')
        setQuery('')
    }

    return (
        <div className={clsx('LeadTitleSelect', className)}>
            <Combobox value={query} onChange={handleOptionSelect}>
                {label && (
                    <ComboboxLabel className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {label}
                    </ComboboxLabel>
                )}
                <div className="relative">
                    <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-300 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 dark:bg-gray-950 dark:border-gray-800 dark:focus-within:border-primary-500 flex items-center">
                        <ComboboxInput
                            value={query}
                            className="flex-1 border-none py-2 pl-3 pr-2 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none dark:bg-gray-950 dark:text-gray-100"
                            placeholder={t('Dr., Prof. or custom...')}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && query.trim()) {
                                    e.preventDefault()
                                    handleConfirm()
                                }
                                if (e.key === 'Escape') {
                                    setQuery(getDisplayValue(title, customTitle))
                                }
                            }}
                        />
                        {isCustomQuery && (
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                                onClick={(e) => { e.stopPropagation(); handleConfirm() }}
                                className="flex items-center gap-1 px-2 py-1 mr-1 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 shrink-0"
                            >
                                <PlusIcon className="h-3.5 w-3.5" />
                                {t('Add')}
                            </button>
                        )}
                        {currentValue && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="flex items-center px-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        )}
                        <ComboboxButton className="flex items-center pr-2">
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </ComboboxButton>
                    </div>
                    {filteredOptions.length > 0 && (
                    <ComboboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50 dark:bg-gray-950 dark:ring-gray-800">
                        {filteredOptions.map((option) => (
                            <ComboboxOption
                                key={option}
                                value={option}
                                className={({ active }) =>
                                    clsx(
                                        'relative cursor-default select-none py-2 pl-3 pr-9',
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900 dark:text-gray-100'
                                    )
                                }
                            >
                                {({ active }) => (
                                    <>
                                        <span
                                            className={clsx(
                                                'block truncate',
                                                currentValue === option ? 'font-semibold' : 'font-normal'
                                            )}
                                        >
                                            {option}
                                        </span>
                                        {currentValue === option && (
                                            <span
                                                className={clsx(
                                                    'absolute inset-y-0 right-0 flex items-center pr-4',
                                                    active ? 'text-white' : 'text-indigo-600'
                                                )}
                                            >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </ComboboxOption>
                        ))}
                    </ComboboxOptions>
                    )}
                </div>
            </Combobox>
        </div>
    )
}

export default LeadTitleSelect
